import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Loader2,
  PlayCircle,
  StickyNote,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import AskInformedPanel from "@/components/academy/AskInformedPanel";
import { api, formatApiError } from "@/api/client";

/**
 * Informed OS Academy — Lesson Player (Phase 1c).
 *
 * Route: `/academy/programs/:programSlug/lessons/:lessonSlug`.
 *
 * Behaviour:
 *   1. Fetches program (published) by slug → derives lesson id + sibling nav.
 *   2. Fetches full lesson (`GET /academy/lessons/{id}`) — triggers server-side
 *      `academy_lesson_started` emission on first-ever open (server enforced).
 *   3. Fetches enrollment progress map (`GET /academy/enrollments/{id}/progress`)
 *      for the sidebar checkmarks.
 *   4. Fetches playback envelope (`GET /academy/lessons/{id}/playback`) for
 *      video-kind lessons only. Renders the honest "video unavailable" state
 *      whenever the envelope is not `ready`.
 *   5. Explicit "Mark Complete" → `POST /academy/lessons/{id}/complete`.
 *      NO auto-completion from watched %.
 *   6. Bookmark toggle → `POST /academy/lessons/{id}/bookmark`.
 *   7. Notes CRUD → `/academy/lessons/{id}/notes` + `/academy/notes/{id}`.
 *   8. Ask Informed drawer entry — rendered only when the
 *      `academy_ask_informed_enabled` flag is on (default off).
 */
export default function LessonPlayer() {
  const { programSlug, lessonSlug } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    status: "loading",
    program: null,
    lesson: null,
    module: null,
    progress: null,
    sibling: { prev: null, next: null },
    enrollment: null,
    progressMap: [],
    playback: null,
    error: null,
  });
  const [completing, setCompleting] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const enrollmentIdRef = useRef(null);

  const findLessonInProgram = useCallback((program, slug) => {
    if (!program || !program.modules) return null;
    for (const mod of program.modules) {
      for (const l of mod.lessons || []) {
        if (l.slug === slug) return { lesson: l, module: mod };
      }
    }
    return null;
  }, []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      // Step 1 — program + enrollments in parallel.
      const [progRes, enrRes] = await Promise.all([
        api.get(`/academy/programs/${programSlug}`),
        api.get(`/academy/enrollments/me`).catch(() => ({ data: { items: [] } })),
      ]);
      const program = progRes.data;
      const enrollments = enrRes.data?.items || [];
      const enrollment = enrollments.find(
        (e) => e.program_id === program.id && (e.status === "active" || e.status === "completed"),
      );
      if (!enrollment) {
        setState({
          status: "not_enrolled", program, lesson: null, module: null,
          progress: null, sibling: { prev: null, next: null },
          enrollment: null, progressMap: [], playback: null, error: null,
        });
        return;
      }
      enrollmentIdRef.current = enrollment.id;

      // Step 2 — locate the lesson id from program.modules.
      const found = findLessonInProgram(program, lessonSlug);
      if (!found) {
        setState({
          status: "lesson_not_found", program, lesson: null, module: null,
          progress: null, sibling: { prev: null, next: null },
          enrollment, progressMap: [], playback: null, error: null,
        });
        return;
      }
      const lessonId = found.lesson.id;

      // Step 3 — full lesson + progress map + notes in parallel.
      const [lessonRes, mapRes, notesRes] = await Promise.all([
        api.get(`/academy/lessons/${lessonId}`),
        api.get(`/academy/enrollments/${enrollment.id}/progress`).catch(() => ({ data: { items: [] } })),
        api.get(`/academy/lessons/${lessonId}/notes`).catch(() => ({ data: { items: [] } })),
      ]);
      const lessonPayload = lessonRes.data;
      const lessonDoc = lessonPayload.lesson;

      // Step 4 — playback envelope for video lessons only.
      let playbackEnv = null;
      if (lessonDoc.kind === "video") {
        const pbRes = await api.get(`/academy/lessons/${lessonId}/playback`).catch(() => ({ data: null }));
        playbackEnv = pbRes.data;
      }

      setState({
        status: "ready",
        program,
        lesson: lessonDoc,
        module: lessonDoc.module || found.module || null,
        progress: lessonPayload.progress,
        sibling: lessonPayload.sibling || { prev: null, next: null },
        enrollment,
        progressMap: mapRes.data?.items || [],
        playback: playbackEnv,
        error: null,
      });
      setNotes(notesRes.data?.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (err?.response?.status === 404 && code === "feature_disabled") {
        setState((s) => ({ ...s, status: "disabled", error: null }));
        return;
      }
      if (err?.response?.status === 403 && code === "not_enrolled") {
        setState((s) => ({ ...s, status: "not_enrolled", error: null }));
        return;
      }
      if (err?.response?.status === 404) {
        setState((s) => ({ ...s, status: "lesson_not_found", error: null }));
        return;
      }
      setState((s) => ({ ...s, status: "error", error: formatApiError(err) }));
    }
  }, [programSlug, lessonSlug, findLessonInProgram]);

  useEffect(() => { load(); }, [load]);

  const onMarkComplete = useCallback(async () => {
    if (!state.lesson) return;
    setCompleting(true);
    try {
      const res = await api.post(`/academy/lessons/${state.lesson.id}/complete`);
      const p = res.data?.progress || null;
      const enr = res.data?.enrollment || null;
      setState((s) => ({ ...s, progress: p, enrollment: enr || s.enrollment }));
      toast.success("Lesson marked complete.");
      // Refresh sidebar checkmarks.
      if (enrollmentIdRef.current) {
        api.get(`/academy/enrollments/${enrollmentIdRef.current}/progress`)
          .then((r) => setState((s) => ({ ...s, progressMap: r.data?.items || [] })))
          .catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't mark complete. Try again.");
    } finally {
      setCompleting(false);
    }
  }, [state.lesson]);

  const onToggleBookmark = useCallback(async () => {
    if (!state.lesson) return;
    setBookmarking(true);
    try {
      const res = await api.post(`/academy/lessons/${state.lesson.id}/bookmark`);
      setState((s) => ({ ...s, progress: res.data?.progress || s.progress }));
    } catch (err) {
      toast.error(formatApiError(err) || "Bookmark update failed.");
    } finally {
      setBookmarking(false);
    }
  }, [state.lesson]);

  const onAddNote = useCallback(async () => {
    const body = (newNote || "").trim();
    if (!body || !state.lesson) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/academy/lessons/${state.lesson.id}/notes`, { body_markdown: body });
      setNotes((prev) => [res.data, ...prev]);
      setNewNote("");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save note.");
    } finally {
      setSavingNote(false);
    }
  }, [newNote, state.lesson]);

  const onDeleteNote = useCallback(async (noteId) => {
    try {
      await api.delete(`/academy/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't delete note.");
    }
  }, []);

  const isCompleted = state.progress?.status === "completed";
  const isBookmarked = Boolean(state.progress?.bookmarked);

  const progressLookup = useMemo(() => {
    const map = new Map();
    for (const item of state.progressMap || []) map.set(item.id, item);
    return map;
  }, [state.progressMap]);

  if (state.status === "loading") {
    return (
      <Shell>
        <div data-testid="academy-lesson-loading" className="flex items-center gap-2 text-v2-text-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading lesson…
        </div>
      </Shell>
    );
  }
  if (state.status === "disabled") {
    return (
      <Shell>
        <EmptyBlock
          testId="academy-lesson-disabled"
          title="Academy is not yet available."
          hint="Please check back later."
          back={{ to: "/dashboard", label: "Back to dashboard" }}
        />
      </Shell>
    );
  }
  if (state.status === "not_enrolled") {
    return (
      <Shell>
        <EmptyBlock
          testId="academy-lesson-not-enrolled"
          title="Enroll to start this lesson."
          hint="You need an active enrollment in the program to open its lessons."
          back={{ to: `/academy/programs/${programSlug}`, label: "Back to program" }}
        />
      </Shell>
    );
  }
  if (state.status === "lesson_not_found") {
    return (
      <Shell>
        <EmptyBlock
          testId="academy-lesson-not-found"
          title="Lesson not found."
          hint="This lesson isn't available or has been unpublished."
          back={{ to: `/academy/programs/${programSlug}`, label: "Back to program" }}
        />
      </Shell>
    );
  }
  if (state.status === "error") {
    return (
      <Shell>
        <div
          data-testid="academy-lesson-error"
          className="rounded-xl border border-[var(--v2-danger)] bg-v2-surface p-6 text-v2-text"
        >
          <p className="font-semibold text-[var(--v2-danger)]">Couldn't load lesson.</p>
          <p className="mt-2 text-sm text-v2-text-muted">{state.error}</p>
          <button
            data-testid="academy-lesson-retry"
            type="button"
            onClick={load}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-v2-border text-sm hover:bg-v2-surface-muted"
          >
            Retry
          </button>
        </div>
      </Shell>
    );
  }

  const lesson = state.lesson;
  const program = state.program;

  return (
    <Shell testId="academy-lesson-player">
      {/* Breadcrumb / back */}
      <div className="mb-6 flex items-center gap-3 text-sm text-v2-text-muted">
        <Link
          to={`/academy/programs/${programSlug}`}
          data-testid="academy-lesson-back-to-program"
          className="inline-flex items-center gap-1.5 hover:text-v2-text"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {program?.title}
        </Link>
        <span>·</span>
        <span className="truncate">{state.module?.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_300px] gap-8">
        {/* Left — curriculum sidebar */}
        <aside data-testid="academy-lesson-sidebar" className="hidden lg:block">
          <div className="rounded-xl border border-v2-border bg-v2-surface p-4 sticky top-24">
            <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted mb-3">
              Curriculum
            </p>
            <ol className="space-y-4">
              {(program?.modules || []).map((mod, mi) => (
                <li key={mod.id}>
                  <p className="text-[11px] uppercase tracking-wider text-v2-text-muted">
                    Module {mi + 1}
                  </p>
                  <p className="text-sm font-medium text-v2-text mt-0.5">{mod.title}</p>
                  <ul className="mt-2 space-y-1">
                    {(mod.lessons || []).map((l) => {
                      const p = progressLookup.get(l.id);
                      const done = p?.progress?.status === "completed";
                      const isActive = l.slug === lessonSlug;
                      return (
                        <li key={l.id}>
                          <Link
                            to={`/academy/programs/${programSlug}/lessons/${l.slug}`}
                            data-testid={`academy-lesson-sidebar-link-${l.slug}`}
                            className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-md transition-colors ${
                              isActive
                                ? "bg-[var(--v2-accent-soft)] text-v2-text font-medium"
                                : "text-v2-text-muted hover:text-v2-text"
                            }`}
                          >
                            {done ? (
                              <Check className="w-3.5 h-3.5 text-[var(--v2-tier-ready)] flex-shrink-0" />
                            ) : (
                              <Circle className="w-3.5 h-3.5 flex-shrink-0" />
                            )}
                            <span className="flex-1 truncate">{l.title}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        {/* Center — player + content */}
        <section data-testid="academy-lesson-main" className="min-w-0">
          <header className="mb-6">
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">
              <span data-testid="academy-lesson-kind">{lesson.kind}</span>
              {lesson.estimated_minutes ? (
                <>
                  <span>·</span>
                  <span>{lesson.estimated_minutes} min</span>
                </>
              ) : null}
            </div>
            <h1
              data-testid="academy-lesson-title"
              className="mt-2 text-[28px] md:text-[36px] font-semibold tracking-tight text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              {lesson.title}
            </h1>
          </header>

          {/* Video area (only for kind == video) */}
          {lesson.kind === "video" ? (
            <div
              data-testid="academy-lesson-video-area"
              className="mb-8 rounded-xl overflow-hidden border border-v2-border bg-black aspect-video"
            >
              {state.playback?.status === "ready" && state.playback.embed_url ? (
                <iframe
                  data-testid="academy-lesson-video-iframe"
                  src={state.playback.embed_url}
                  title={lesson.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  data-testid="academy-lesson-video-unavailable"
                  className="w-full h-full flex flex-col items-center justify-center gap-3 bg-v2-surface text-v2-text-muted p-8 text-center"
                >
                  <PlayCircle className="w-8 h-8" />
                  <p className="text-sm font-semibold text-v2-text">Video unavailable</p>
                  <p className="text-xs">
                    Video playback isn't ready yet for this lesson.
                    {state.playback?.reason ? (
                      <>
                        {" "}Reason:{" "}
                        <code
                          data-testid="academy-lesson-video-reason"
                          className="text-[11px] px-1.5 py-0.5 rounded bg-v2-surface-muted"
                        >
                          {state.playback.reason}
                        </code>
                      </>
                    ) : null}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {/* Body markdown */}
          {lesson.body_markdown ? (
            <article
              data-testid="academy-lesson-body"
              className="rounded-xl border border-v2-border bg-v2-surface p-6 md:p-8 prose prose-sm max-w-none text-v2-text whitespace-pre-wrap"
            >
              {lesson.body_markdown}
            </article>
          ) : (
            <div
              data-testid="academy-lesson-body-empty"
              className="rounded-xl border border-v2-border bg-v2-surface p-8 text-center text-sm text-v2-text-muted"
            >
              Lesson content coming soon.
            </div>
          )}

          {/* Actions */}
          <div
            data-testid="academy-lesson-actions"
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              onClick={onMarkComplete}
              disabled={completing || isCompleted}
              data-testid="academy-lesson-mark-complete-btn"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold ${
                isCompleted
                  ? "bg-v2-surface-muted text-[var(--v2-tier-ready)] border border-v2-border cursor-default"
                  : "bg-[var(--v2-accent)] text-white hover:opacity-90 disabled:opacity-50"
              }`}
            >
              {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : <Check className="w-4 h-4" />}
              {isCompleted ? "Completed" : completing ? "Marking…" : "Mark Complete"}
            </button>
            <button
              type="button"
              onClick={onToggleBookmark}
              disabled={bookmarking}
              data-testid="academy-lesson-bookmark-btn"
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-v2-border text-sm text-v2-text hover:bg-v2-surface-muted disabled:opacity-50"
            >
              {isBookmarked ? (
                <BookmarkCheck className="w-4 h-4 text-[var(--v2-accent)]" />
              ) : (
                <Bookmark className="w-4 h-4" />
              )}
              {isBookmarked ? "Bookmarked" : "Bookmark"}
            </button>
          </div>

          {/* Prev / Next */}
          <nav
            data-testid="academy-lesson-nav"
            className="mt-8 flex items-center justify-between border-t border-v2-border pt-6"
          >
            {state.sibling?.prev ? (
              <Link
                to={`/academy/programs/${programSlug}/lessons/${state.sibling.prev.slug}`}
                data-testid="academy-lesson-nav-prev"
                className="inline-flex items-center gap-2 text-sm text-v2-text hover:text-v2-accent min-w-0"
              >
                <ChevronLeft className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{state.sibling.prev.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {state.sibling?.next ? (
              <Link
                to={`/academy/programs/${programSlug}/lessons/${state.sibling.next.slug}`}
                data-testid="academy-lesson-nav-next"
                className="inline-flex items-center gap-2 text-sm text-v2-text hover:text-v2-accent min-w-0 justify-end"
              >
                <span className="truncate">{state.sibling.next.title}</span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
              </Link>
            ) : (
              <span
                data-testid="academy-lesson-nav-next-end"
                className="text-xs text-v2-text-muted italic"
              >
                Last lesson in program
              </span>
            )}
          </nav>
        </section>

        {/* Right — notes + ask informed */}
        <aside data-testid="academy-lesson-rail" className="space-y-4">
          <div className="rounded-xl border border-v2-border bg-v2-surface p-5">
            <div className="flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-v2-text-muted" />
              <p
                className="text-sm font-semibold text-v2-text"
                style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
              >
                Notes
              </p>
            </div>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Jot a note about this lesson…"
              data-testid="academy-lesson-note-input"
              className="mt-3 w-full h-24 rounded-lg border border-v2-border bg-v2-background p-3 text-sm text-v2-text resize-none focus:outline-none focus:border-[var(--v2-accent)]"
            />
            <button
              type="button"
              onClick={onAddNote}
              disabled={savingNote || !newNote.trim()}
              data-testid="academy-lesson-note-save-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[var(--v2-accent)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {savingNote ? "Saving…" : "Save note"}
            </button>
            {notes.length === 0 ? (
              <p
                data-testid="academy-lesson-notes-empty"
                className="mt-4 text-xs text-v2-text-muted italic"
              >
                No notes yet.
              </p>
            ) : (
              <ul data-testid="academy-lesson-notes-list" className="mt-4 space-y-3">
                {notes.map((n) => (
                  <li
                    key={n.id}
                    data-testid={`academy-lesson-note-${n.id}`}
                    className="rounded-lg border border-v2-border bg-v2-background p-3"
                  >
                    <p className="text-sm text-v2-text whitespace-pre-wrap">{n.body_markdown}</p>
                    <button
                      type="button"
                      onClick={() => onDeleteNote(n.id)}
                      data-testid={`academy-lesson-note-delete-${n.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-v2-text-muted hover:text-[var(--v2-danger)]"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <AskInformedPanel
            programId={program?.id}
            moduleId={state.module?.id}
            lessonId={lesson.id}
          />
        </aside>
      </div>
    </Shell>
  );
}

function Shell({ children, testId = "academy-lesson-player-shell" }) {
  return (
    <div data-testid={testId} className="min-h-screen bg-v2-background text-v2-text">
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 md:px-8 pt-12 pb-24">{children}</main>
    </div>
  );
}

function EmptyBlock({ testId, title, hint, back }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
    >
      <BookOpen className="w-8 h-8 mx-auto text-v2-text-muted" />
      <p className="mt-4 text-v2-text font-semibold">{title}</p>
      <p className="mt-2 text-sm text-v2-text-muted">{hint}</p>
      {back ? (
        <Link
          to={back.to}
          data-testid={`${testId}-back`}
          className="mt-6 inline-flex items-center gap-2 text-sm text-v2-accent hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          {back.label}
        </Link>
      ) : null}
    </div>
  );
}
